import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import cx from 'classnames';
import { Button, Icon, Text } from 'components/@tedui';
import ContentContainer from 'components/content-container';
import { Honeypot } from 'components/footer/Honeypot';
import { PrismicNextImage } from 'components/PRISMIC/PrismicNextImage';
import { Link } from 'components/router';
import { getSystemLanguage } from 'lib/analytics/mixpanel-helpers';
import { MASTER_LIST } from 'lib/hooks/useSailthru/const';
import { getOptinDate } from 'lib/hooks/useSailthru/utils';
import { isValidEmail } from 'lib/util';
import { useState } from 'react';

/**
 * Props for `Newsletter`.
 */
export type NewsletterProps = SliceComponentProps<Content.NewsletterSlice>;

const NewsletterRibbonLabels = {
  privacyPolicy: 'By subscribing, you accept the terms of '
};

/**
 * Component for "Newsletter" Slices.
 */
const Newsletter = ({ slice }: NewsletterProps): JSX.Element => {
  const [email, setEmail] = useState('');
  const [honeypotChecked, setHoneypotChecked] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isValid, setIsValid] = useState(true);

  const hasImage = slice.variation === 'withImage';
  const isDarkStyle = slice.primary.dark_style;
  const primary = slice.primary;

  const onSubmit = () => {
    const isValid = isValidEmail(email);

    if (!isValid) {
      setIsValid(false);
      return;
    }

    signUp({
      [primary.optin_date_value as string]: getOptinDate(),
      [primary.source as string]: primary.source_value ?? ''
    });
  };

  const signUp = (vars: { [key: string]: string }) => {
    if (honeypotChecked) return;

    if (window.Sailthru === undefined) {
      console.error('Sailthru is not defined');
      return;
    }

    window.Sailthru.integration('userSignUp', {
      email,
      lists: {
        ...MASTER_LIST,
        [slice.primary.list_name as string]: 1
      },
      vars: vars,
      source: 'web',
      onSuccess: () => {
        setEmail('');
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      },
      onError: () => {
        console.error('Error subscribing to newsletter');
      }
    });
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const isEmailValid = isValidEmail(value);
    setIsValid(isEmailValid);
    setEmail(value);
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <ContentContainer
        className={cx(
          'pb-16 pt-8 lg-tui:pb-20 lg-tui:pt-10',
          isDarkStyle ? 'bg-textSecondary-onLight' : 'bg-gray-300'
        )}
      >
        <div className="h-0">
          <Honeypot onGrab={setHoneypotChecked} />
        </div>
        <div
          className={cx(
            'mb-4 h-px w-full md-tui:mb-6',
            isDarkStyle ? 'bg-white' : 'bg-black'
          )}
        />
        <div className="bg-gray-100 flex flex-col justify-between gap-6 md-tui:gap-10 lg-tui:flex-row lg-tui:gap-6">
          {showSuccess ? (
            <Text
              variant="header2"
              UNSAFE_className={cx('flex-1', isDarkStyle && 'text-white')}
              useNewTextStyles
            >
              Thanks for subscribing!
            </Text>
          ) : (
            <Text
              variant="header2"
              UNSAFE_className={cx('flex-1', isDarkStyle && 'text-white')}
              useNewTextStyles
            >
              {primary.title}
            </Text>
          )}

          <div
            className={cx('flex flex-1 flex-col', {
              'gap-14': !hasImage,
              'gap-10': hasImage
            })}
          >
            {slice.variation === 'withImage' &&
              'image' in primary &&
              primary.image?.url && (
                <div className="flex gap-4">
                  <div className="aspect-h-1 aspect-w-1 size-[93px] min-w-[93px] overflow-hidden">
                    <PrismicNextImage
                      alt={
                        primary.image.alt || primary.side_title || 'Newsletter'
                      }
                      field={primary.image}
                      width={92}
                      height={92}
                      className="size-full object-cover"
                    />
                  </div>
                  <div className="mr-4 flex flex-col">
                    {primary.side_title && (
                      <Text
                        variant="header4"
                        useNewTextStyles
                        UNSAFE_className={cx(
                          'mb-2',
                          isDarkStyle && 'text-white'
                        )}
                      >
                        {primary.side_title}
                      </Text>
                    )}
                    {primary.side_text && (
                      <Text
                        variant="body2"
                        useNewTextStyles
                        UNSAFE_className={cx(
                          'mb-1',
                          isDarkStyle && 'text-white'
                        )}
                      >
                        {primary.side_text}
                      </Text>
                    )}
                  </div>
                </div>
              )}

            {!hasImage && (
              <div className="flex flex-col gap-1">
                {primary.side_title && (
                  <Text
                    variant="header3"
                    tag="h3"
                    weight="font-semibold"
                    useNewTextStyles
                    UNSAFE_className={cx(isDarkStyle && 'text-white')}
                  >
                    {primary.side_title}
                  </Text>
                )}
                {primary.side_text && (
                  <Text
                    variant="subheader1"
                    useNewTextStyles
                    UNSAFE_className={cx(isDarkStyle && 'text-white')}
                  >
                    {primary.side_text}
                  </Text>
                )}
              </div>
            )}

            <section className="flex flex-col gap-6">
              <div className="flex w-full flex-col items-center gap-4 md-tui:flex-row md-tui:items-start">
                <div className="flex w-full flex-col md-tui:w-[341px] lg-tui:w-full">
                  <div className="relative w-full">
                    <input
                      onChange={handleEmailChange}
                      placeholder="What is your email?"
                      id="newsletter-email"
                      className={cx(
                        'flex h-12 w-full flex-1 border-thin bg-white py-3 pl-3 pr-12 text-sm placeholder:text-gray-700 focus:outline-none',
                        {
                          'border-systemInfo-error-onLight focus:border-thin focus:border-systemInfo-error-onLight':
                            !isValid,
                          'border-systemInfo-success focus:border-systemInfo-success':
                            isValid && email.length > 0
                        }
                      )}
                      type="email"
                      value={email}
                    />
                    {!isValid && (
                      <Icon
                        iconName="alert-triangle"
                        className="absolute right-2 top-1/2 mr-2 -translate-y-1/2 transform text-tui-xl text-systemInfo-error-onDark"
                      />
                    )}
                  </div>

                  {!isValid && (
                    <Text
                      tag="p"
                      variant="caption"
                      UNSAFE_className="mt-1 ml-2"
                      color={{
                        color: 'error'
                      }}
                    >
                      <span aria-live="polite">
                        Please add a valid email address
                      </span>
                    </Text>
                  )}
                </div>

                <Button
                  variant="primary"
                  text={primary.cta_button_text || 'Subscribe Now'}
                  UNSAFE_className="md-tui:min-w-35 min-w-full !h-12"
                  isFullWidth={false}
                  onClick={() => onSubmit()}
                />
              </div>

              <Text
                useNewTextStyles
                variant="body2"
                tag="span"
                color={{ isOnLightSurface: false }}
              >
                {NewsletterRibbonLabels.privacyPolicy}
                <Link
                  href="/about/our-organization/our-policies-terms/privacy-policy"
                  className="underline"
                  onClick={() => {
                    mixpanel.track('global_footer_click', {
                      event_interaction_context: 'newsletters_privacy-policy',
                      system_language: getSystemLanguage()
                    });
                  }}
                >
                  TED's Privacy Policy
                </Link>
              </Text>
            </section>
          </div>
        </div>
      </ContentContainer>
    </section>
  );
};

export default Newsletter;
